import type { ResultOf } from "gql.tada";
import type { StateCreator } from "zustand";
import { create } from "zustand";
import { type DevtoolsOptions, devtools } from "zustand/middleware";
import type {
  GetAllNewsFeeds,
  NewsFeedTermsType,
} from "@/data-access/news-feeds/queries/news-feeds-queries";
import { createNewsFeedActions } from "./news-feed.actions";

export type NewsFeed = ResultOf<typeof GetAllNewsFeeds>["feeds"][0];

/**
 * News feeds store slice that manages all feeds and their CRUD operations
 * Handles feed listing, creation, updates, deletion and related UI states panels
 */

// Modal State Slice
type ModalStateSlice = {
  isCreateFeedModalOpen: boolean;
  isEditFeedModalOpen: boolean;
  isDeleteFeedModalOpen: boolean;
  editingFeedId: string | null;
};

// Creation and Update State Slice
type CreateAndUpdateStateSlice = {
  createFeedState: "initial" | "complete";
  updateFeedState: "initial" | "complete";
  isCreatingFeed: boolean;
  isUpdatingFeed: boolean;
  isDeletingFeed: boolean;
};

// Data Fetch Slice
type DataFetchSlice = {
  allNewsFeeds: NewsFeed[];
  isFetchingNewsFeeds: boolean;
};

// Form State Slice - Stores form data for editing/creating feeds
type NewsFeedFormStateSlice = {
  editingFeedId: string | null;
  formFeedName: string;
  formKeyTerms: NewsFeedTermsType[];
  setEditingFeedId: (feedId: string | null) => void;
  setFormFeedName: (name: string) => void;
  setFormKeyTerms: (terms: NewsFeedTermsType[]) => void;
  resetFormState: () => void;
};

// Feed State Slice
type FeedStateSlice = {
  currentFeedId: string | null;
  setFeedId: (feedId: string | null) => void;
  resetFeed: () => void;
};

// Panel State Slice
type PanelStateSlice = {
  isPanelOpen: boolean;
  panelMode: "add" | "edit" | null;
  openAddPanel: () => void;
  openEditPanel: () => void;
  closePanel: () => void;
};

export type NewsFeedsSlice = ModalStateSlice &
  CreateAndUpdateStateSlice &
  DataFetchSlice &
  NewsFeedFormStateSlice &
  FeedStateSlice &
  PanelStateSlice;

// Initial state objects
const modalInitialState = {
  isCreateFeedModalOpen: false,
  isEditFeedModalOpen: false,
  isDeleteFeedModalOpen: false,
  editingFeedId: null,
};

const createAndUpdateInitialState = {
  createFeedState: "initial" as const,
  updateFeedState: "initial" as const,
  isCreatingFeed: false,
  isUpdatingFeed: false,
  isDeletingFeed: false,
};

const dataFetchInitialState = {
  allNewsFeeds: [],
  isFetchingNewsFeeds: false,
};

const formInitialState = {
  editingFeedId: null,
  formFeedName: "",
  formKeyTerms: [] as NewsFeedTermsType[],
};

const feedInitialState = {
  currentFeedId: null,
};

const panelInitialState = {
  isPanelOpen: false,
  panelMode: null as "add" | "edit" | null,
};

const createNewsFeedsSlice: StateCreator<
  NewsFeedsSlice,
  [["zustand/devtools", never]],
  [],
  NewsFeedsSlice
> = (set, get, api) => ({
  ...modalInitialState,
  ...createAndUpdateInitialState,
  ...dataFetchInitialState,
  ...formInitialState,
  ...feedInitialState,
  ...panelInitialState,

  ...createNewsFeedActions(set, get, api),
});

// news feeds store
const useNewsFeedsStore = create<NewsFeedsSlice>()(
  devtools(createNewsFeedsSlice, {
    name: "news-feed-management-store",
    serialize: {
      options: true,
    },
  } as unknown as DevtoolsOptions),
);

export default useNewsFeedsStore;
